import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useConfirm } from "../contexts/ConfirmProvider";
import { useNavigate, Link } from "react-router-dom";
import { extractError } from "../lib/errors";
import {
  Crown, Loader2, Plus, Trash2, Edit2, Save, X, Search, AlertTriangle,
  Building2, Users, ShieldCheck, BarChart3, History, Megaphone, Star,
  CheckCircle2, XCircle, Lock, Unlock, DollarSign, Plug, Mail, Brain, Send,
  CreditCard, Inbox, ExternalLink, RefreshCw, RotateCcw, KeyRound,
} from "lucide-react";
import ConfirmDestructiveDialog from "../components/ConfirmDestructiveDialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const PlatformAdmin = () => {
  const { token, user } = useAuth();
  const { locale, t } = useI18n();
  const isES = locale === "es";
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [knownFlags, setKnownFlags] = useState({ features: [], limits: [] });
  const [banner, setBanner] = useState({ enabled: false, message: "", severity: "info" });
  const [loading, setLoading] = useState(true);
  const [searchOrgs, setSearchOrgs] = useState("");
  const [searchUsers, setSearchUsers] = useState("");

  const [editingTier, setEditingTier] = useState(null); // tier doc or {new:true}
  const [grantEmail, setGrantEmail] = useState("");

  // S14a: org CRUD dialogs
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [createOrgForm, setCreateOrgForm] = useState({ name: "", owner_email: "", owner_name: "", tier_id: "" });
  const [createOrgLoading, setCreateOrgLoading] = useState(false);
  const [deleteOrgTarget, setDeleteOrgTarget] = useState(null); // org object
  const [deleteOrgLoading, setDeleteOrgLoading] = useState(false);
  const [restoreOrgTarget, setRestoreOrgTarget] = useState(null);
  const [restoreOrgLoading, setRestoreOrgLoading] = useState(false);
  const [resetPwdTarget, setResetPwdTarget] = useState(null); // user object
  const [resetPwdLoading, setResetPwdLoading] = useState(false);

  // P2 — tier-override form (replaces 3 sequential window.prompt calls, §2c)
  const [overrideTarget, setOverrideTarget] = useState(null); // org object
  const [overrideForm, setOverrideForm] = useState({ tier: "", priceOverride: "", note: "" });
  const [overrideLoading, setOverrideLoading] = useState(false);

  // Hard guard — if not super-admin, redirect
  useEffect(() => {
    if (user && !user.is_super_admin) {
      toast.error(isES ? "Acceso restringido a super-administradores" : "Reserved for super-admins");
      navigate("/dashboard");
    }
  }, [user, navigate, isES]);

  const authH = { headers: { Authorization: `Bearer ${token}` } };

  const fetchAll = useCallback(async () => {
    if (!token || !user?.is_super_admin) { setLoading(false); return; }
    try {
      const [s, t, a, o, u, al, k, b] = await Promise.all([
        axios.get(`${API}/platform/stats`, authH),
        axios.get(`${API}/platform/tiers`, authH),
        axios.get(`${API}/platform/admins`, authH),
        axios.get(`${API}/platform/organizations`, authH),
        axios.get(`${API}/platform/users`, authH),
        axios.get(`${API}/platform/audit-log?limit=50`, authH),
        axios.get(`${API}/platform/tiers/meta/known-flags`, authH),
        axios.get(`${API}/platform/system/banner`, authH),
      ]);
      setStats(s.data);
      setTiers(t.data);
      setAdmins(a.data);
      setOrgs(o.data);
      setUsers(u.data);
      setAuditLog(al.data);
      setKnownFlags(k.data);
      setBanner(b.data || { enabled: false, message: "", severity: "info" });
    } catch (e) {
      toast.error(isES ? "Error al cargar datos" : "Failed to load data");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---------- Tiers ----------
  const startEditTier = (tier) => setEditingTier(JSON.parse(JSON.stringify(tier)));
  const startNewTier = () => setEditingTier({
    tier_id: "",
    name: "",
    price_monthly: 0,
    currency: "USD",
    color: "#3B82F6",
    popular: false,
    active: true,
    description: "",
    price_per_standard_monthly: 0,
    price_per_user_monthly: 0,
    price_per_extra_framework: 350,
    included_standards: 0,
    included_users: 0,
    frameworks_max: 1,
    trial_days: 0,
    limits: Object.fromEntries(knownFlags.limits.map((k) => [k, 0])),
    features: Object.fromEntries(knownFlags.features.map((k) => [k, false])),
    new: true,
  });

  const saveTier = async () => {
    try {
      if (editingTier.new) {
        const payload = { ...editingTier };
        delete payload.new;
        await axios.post(`${API}/platform/tiers`, payload, authH);
        toast.success(isES ? "Plan creado" : "Plan created");
      } else {
        const { tier_id, org_count, created_at, updated_at, ...payload } = editingTier;
        await axios.put(`${API}/platform/tiers/${tier_id}`, payload, authH);
        toast.success(isES ? "Plan actualizado" : "Plan updated");
      }
      setEditingTier(null);
      fetchAll();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error" : "Failed"));
    }
  };

  const deleteTier = async (tier) => {
    if (!(await confirm({
      title: t("padmin.delete_tier_title"),
      description: `${t("padmin.delete_tier_desc_prefix")} "${tier.name}"${t("padmin.delete_tier_desc_suffix")}`,
      destructive: true,
    }))) return;
    try {
      await axios.delete(`${API}/platform/tiers/${tier.tier_id}`, authH);
      toast.success(isES ? "Plan eliminado" : "Plan deleted");
      fetchAll();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error" : "Failed"));
    }
  };

  const resetTiersToDefaults = async () => {
    if (!(await confirm({
      title: t("padmin.reset_tiers_title"),
      description: t("padmin.reset_tiers_desc"),
      destructive: true,
    }))) return;
    try {
      const res = await axios.post(`${API}/platform/tiers/reset-to-defaults`, {}, authH);
      toast.success(isES
        ? `Listo: ${res.data.deleted_stale} eliminados, ${res.data.reseeded} restaurados`
        : `Done: ${res.data.deleted_stale} removed, ${res.data.reseeded} reseeded`);
      fetchAll();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error" : "Failed"));
    }
  };

  // ---------- Super-admins ----------
  const grantAdmin = async () => {
    if (!grantEmail) return;
    try {
      await axios.post(`${API}/platform/admins/grant`, { email: grantEmail }, authH);
      toast.success(isES ? "Permiso otorgado" : "Permission granted");
      setGrantEmail("");
      fetchAll();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error" : "Failed"));
    }
  };
  const revokeAdmin = async (email) => {
    if (!(await confirm({
      title: t("padmin.revoke_admin_title"),
      description: `${t("padmin.revoke_admin_desc_prefix")} ${email}${t("padmin.revoke_admin_desc_suffix")}`,
      destructive: true,
    }))) return;
    try {
      await axios.post(`${API}/platform/admins/revoke`, { email }, authH);
      toast.success(isES ? "Permiso revocado" : "Permission revoked");
      fetchAll();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error" : "Failed"));
    }
  };

  // ---------- Orgs (S14a) ----------
  const toggleSuspend = async (org) => {
    const newSuspended = !org.suspended;
    try {
      await axios.put(
        `${API}/platform/organizations/${org.organization_id}/suspend`,
        { suspended: newSuspended, reason: "" },
        authH,
      );
      toast.success(newSuspended ? (isES ? "Suspendida" : "Suspended") : (isES ? "Reactivada" : "Reactivated"));
      fetchAll();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al suspender" : "Failed to suspend"));
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setCreateOrgLoading(true);
    try {
      const payload = { ...createOrgForm };
      if (!payload.tier_id) delete payload.tier_id;
      if (!payload.owner_name) delete payload.owner_name;
      await axios.post(`${API}/platform/organizations`, payload, authH);
      toast.success(isES ? "Organización creada" : "Organization created");
      setCreateOrgOpen(false);
      setCreateOrgForm({ name: "", owner_email: "", owner_name: "", tier_id: "" });
      fetchAll();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al crear" : "Failed to create"));
    } finally {
      setCreateOrgLoading(false);
    }
  };

  const handleDeleteOrg = async () => {
    if (!deleteOrgTarget) return;
    setDeleteOrgLoading(true);
    try {
      await axios.delete(
        `${API}/platform/organizations/${deleteOrgTarget.organization_id}`,
        { ...authH, data: { deletion_reason: "" } },
      );
      toast.success(isES ? "Organización eliminada (soft delete)" : "Organization soft-deleted");
      setDeleteOrgTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al eliminar" : "Failed to delete"));
    } finally {
      setDeleteOrgLoading(false);
    }
  };

  const handleRestoreOrg = async () => {
    if (!restoreOrgTarget) return;
    setRestoreOrgLoading(true);
    try {
      await axios.post(
        `${API}/platform/organizations/${restoreOrgTarget.organization_id}/restore`,
        {},
        authH,
      );
      toast.success(isES ? "Organización restaurada" : "Organization restored");
      setRestoreOrgTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al restaurar" : "Failed to restore"));
    } finally {
      setRestoreOrgLoading(false);
    }
  };

  const handleAdminResetPassword = async () => {
    if (!resetPwdTarget) return;
    setResetPwdLoading(true);
    try {
      await axios.post(`${API}/platform/users/${resetPwdTarget.user_id}/reset-password`, {}, authH);
      toast.success(
        isES
          ? `Se envió un enlace a ${resetPwdTarget.email}`
          : `Link sent to ${resetPwdTarget.email}`
      );
      setResetPwdTarget(null);
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al enviar enlace" : "Failed to send link"));
    } finally {
      setResetPwdLoading(false);
    }
  };

  // P2 — los 3 window.prompt secuenciales pasan a 1 Dialog-form (§2c del diseño);
  // MISMA llamada de red y MISMA transformación del body (overrideStr === "" ? null :
  // parseFloat / note || null) que la versión original.
  const openOverrideTier = (org) => {
    setOverrideForm({ tier: org.tier || "starter", priceOverride: "", note: "" });
    setOverrideTarget(org);
  };

  const submitOverrideTier = async () => {
    if (!overrideTarget || !overrideForm.tier) return;
    setOverrideLoading(true);
    try {
      await axios.put(
        `${API}/platform/organizations/${overrideTarget.organization_id}/tier-override`,
        {
          tier: overrideForm.tier,
          price_override_monthly: overrideForm.priceOverride === "" ? null : parseFloat(overrideForm.priceOverride),
          note: overrideForm.note || null,
        },
        authH
      );
      toast.success(isES ? "Plan asignado" : "Tier assigned");
      setOverrideTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error" : "Failed"));
    } finally {
      setOverrideLoading(false);
    }
  };

  // ---------- Users ----------
  const toggleUserDisabled = async (u) => {
    const newDisabled = !u.disabled;
    try {
      await axios.put(`${API}/platform/users/${u.user_id}/disable?disabled=${newDisabled}`, {}, authH);
      toast.success(newDisabled ? (isES ? "Usuario deshabilitado" : "User disabled") : (isES ? "Usuario habilitado" : "User enabled"));
      fetchAll();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error" : "Failed"));
    }
  };

  // ---------- Banner ----------
  const saveBanner = async () => {
    try {
      await axios.put(`${API}/platform/system/banner`, banner, authH);
      toast.success(isES ? "Banner guardado" : "Banner saved");
    } catch (e) {
      toast.error(isES ? "Error" : "Failed");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  if (!user?.is_super_admin) return null;

  const filteredOrgs = orgs.filter(o => !searchOrgs ||
    (o.name || "").toLowerCase().includes(searchOrgs.toLowerCase()) ||
    (o.organization_id || "").toLowerCase().includes(searchOrgs.toLowerCase()));
  const filteredUsers = users.filter(u => !searchUsers ||
    (u.email || "").toLowerCase().includes(searchUsers.toLowerCase()) ||
    (u.name || "").toLowerCase().includes(searchUsers.toLowerCase()));

  return (
    <div className="p-6 space-y-6" data-testid="platform-admin-page">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Crown className="w-8 h-8 text-amber-500" />
            {isES ? "Administración de Plataforma" : "Platform Administration"}
          </span>
        }
        description={isES
          ? "Control de planes, organizaciones, usuarios y configuración global del SaaS"
          : "Plans, organizations, users and SaaS-level configuration"}
        actions={
          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
            <Crown className="w-3 h-3 mr-1" />
            Super-admin · {user.email}
          </Badge>
        }
      />

      <Tabs defaultValue="dashboard">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="w-4 h-4 mr-1" /> {isES ? "Resumen" : "Dashboard"}
          </TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">
            <DollarSign className="w-4 h-4 mr-1" /> {isES ? "Planes" : "Plans"}
          </TabsTrigger>
          <TabsTrigger value="orgs" data-testid="tab-orgs">
            <Building2 className="w-4 h-4 mr-1" /> {isES ? "Organizaciones" : "Organizations"}
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="w-4 h-4 mr-1" /> {isES ? "Usuarios" : "Users"}
          </TabsTrigger>
          <TabsTrigger value="admins" data-testid="tab-admins">
            <ShieldCheck className="w-4 h-4 mr-1" /> {isES ? "Super-Admins" : "Super-Admins"}
          </TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">
            <Plug className="w-4 h-4 mr-1" /> {isES ? "Integraciones" : "Integrations"}
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">
            <CreditCard className="w-4 h-4 mr-1" /> {isES ? "Facturacion" : "Billing"}
          </TabsTrigger>
          <TabsTrigger value="system" data-testid="tab-system">
            <Megaphone className="w-4 h-4 mr-1" /> {isES ? "Sistema" : "System"}
          </TabsTrigger>
          <TabsTrigger value="demo-requests" data-testid="tab-demo-requests">
            <Inbox className="w-4 h-4 mr-1" /> {isES ? "Solicitudes Demo" : "Demo Requests"}
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <History className="w-4 h-4 mr-1" /> {isES ? "Auditoría" : "Audit Log"}
          </TabsTrigger>
        </TabsList>

        {/* Demo Requests tab */}
        <TabsContent value="demo-requests" className="mt-4 space-y-4">
          <DemoRequestsTab token={token} isES={isES} />
        </TabsContent>

        {/* Integrations tab */}
        <TabsContent value="integrations" className="mt-4 space-y-4">
          <IntegrationsTab token={token} isES={isES} />
        </TabsContent>

        {/* Billing (PayPal) tab */}
        <TabsContent value="billing" className="mt-4 space-y-4">
          <PayPalBillingTab token={token} isES={isES} />
        </TabsContent>

        {/* Dashboard tab */}
        <TabsContent value="dashboard" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-4 pb-4">
              <div className="text-3xl font-heading font-bold text-primary">
                ${stats?.mrr?.toFixed(0) ?? 0}
              </div>
              <div className="text-xs text-muted-foreground mt-1">MRR (USD)</div>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-4">
              <div className="text-3xl font-heading font-bold">{stats?.total_orgs ?? 0}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {isES ? "Organizaciones" : "Organizations"}
                {stats?.suspended_orgs > 0 && (
                  <span className="text-red-500 ml-1">({stats.suspended_orgs} {isES ? "susp." : "susp."})</span>
                )}
              </div>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-4">
              <div className="text-3xl font-heading font-bold text-blue-500">{stats?.total_users ?? 0}</div>
              <div className="text-xs text-muted-foreground mt-1">{isES ? "Usuarios" : "Users"}</div>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-4">
              <div className="text-3xl font-heading font-bold text-amber-500">{stats?.super_admins ?? 0}</div>
              <div className="text-xs text-muted-foreground mt-1">Super-Admins</div>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{isES ? "Distribución por plan" : "Tier distribution"}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(stats?.distribution || {}).map(([tid, count]) => {
                  const tier = tiers.find(t => t.tier_id === tid);
                  const total = stats?.total_orgs || 1;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={tid} className="flex items-center gap-3" data-testid={`distribution-${tid}`}>
                      <div className="w-32 text-sm font-medium">{tier?.name || tid}</div>
                      <div className="flex-1 h-2 rounded bg-muted relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 rounded"
                             style={{ width: `${pct}%`, backgroundColor: tier?.color || "#3B82F6" }} />
                      </div>
                      <div className="w-20 text-right text-sm tabular-nums">{count} ({pct}%)</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Plans tab */}
        <TabsContent value="plans" className="mt-4 space-y-4">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={resetTiersToDefaults} data-testid="reset-tiers-btn">
              {isES ? "Restaurar predeterminados" : "Reset to defaults"}
            </Button>
            <Button onClick={startNewTier} data-testid="new-tier-btn">
              <Plus className="w-4 h-4 mr-1" />
              {isES ? "Nuevo plan" : "New plan"}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers.map((t) => (
              <Card key={t.tier_id} data-testid={`tier-${t.tier_id}`}
                    className={`border ${t.popular ? "border-primary" : "border-border"} relative`}>
                {t.popular && (
                  <Badge className="absolute -top-2 left-3 bg-primary">
                    <Star className="w-3 h-3 mr-1" /> {isES ? "Popular" : "Popular"}
                  </Badge>
                )}
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle style={{ color: t.color }}>{t.name}</CardTitle>
                    <Badge variant="outline" className="text-[10px]">{t.tier_id}</Badge>
                  </div>
                  <CardDescription>{t.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold">
                    ${t.price_monthly}
                    <span className="text-sm font-normal text-muted-foreground">/mo</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t.org_count || 0} {isES ? "organizaciones activas" : "active organizations"}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => startEditTier(t)}
                            data-testid={`edit-tier-${t.tier_id}`}>
                      <Edit2 className="w-3 h-3 mr-1" />
                      {isES ? "Editar" : "Edit"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteTier(t)}
                            data-testid={`delete-tier-${t.tier_id}`}>
                      <Trash2 className="w-3 h-3 mr-1 text-destructive" />
                      {isES ? "Eliminar" : "Delete"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Orgs tab */}
        <TabsContent value="orgs" className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={searchOrgs} onChange={(e) => setSearchOrgs(e.target.value)}
                     placeholder={isES ? "Buscar organización…" : "Search organization…"}
                     className="pl-9" data-testid="search-orgs" />
            </div>
            <Button size="sm" onClick={() => setCreateOrgOpen(true)} data-testid="create-org-btn">
              <Plus className="w-4 h-4 mr-1" />
              {isES ? "Nuevo tenant" : "New tenant"}
            </Button>
          </div>
          <Card>
            <CardContent className="pt-4">
              <div className="divide-y divide-border">
                {filteredOrgs.map((o) => (
                  <div key={o.organization_id}
                       className="py-3 flex items-center gap-3 flex-wrap"
                       data-testid={`org-row-${o.organization_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        {o.name || o.organization_id}
                        {o.suspended && <Badge variant="destructive" className="text-[10px]">SUSPENDED</Badge>}
                        {o.deleted_at && <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-500 border-red-500/30">DELETED</Badge>}
                        <Badge variant="outline" className="text-[10px]">
                          {tiers.find(t => t.tier_id === o.tier)?.name || o.tier}
                        </Badge>
                        {o.price_override_monthly !== null && o.price_override_monthly !== undefined && (
                          <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/30">
                            Override ${o.price_override_monthly}/mo
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {o.organization_id} · {o.user_count} {isES ? "usuarios" : "users"}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openOverrideTier(o)}
                            data-testid={`override-${o.organization_id}`}>
                      {isES ? "Asignar plan" : "Assign plan"}
                    </Button>
                    {o.deleted_at ? (
                      <Button size="sm" variant="outline" onClick={() => setRestoreOrgTarget(o)}
                              data-testid={`restore-${o.organization_id}`}>
                        <RotateCcw className="w-3 h-3 mr-1" />
                        {isES ? "Restaurar" : "Restore"}
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => toggleSuspend(o)}
                                data-testid={`suspend-${o.organization_id}`}>
                          {o.suspended ? <Unlock className="w-3 h-3 mr-1" /> : <Lock className="w-3 h-3 mr-1" />}
                          {o.suspended
                            ? (isES ? "Reactivar" : "Reactivate")
                            : (isES ? "Suspender" : "Suspend")}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteOrgTarget(o)}
                                data-testid={`delete-org-${o.organization_id}`}>
                          <Trash2 className="w-3 h-3 mr-1 text-destructive" />
                          {isES ? "Eliminar" : "Delete"}
                        </Button>
                      </>
                    )}
                  </div>
                ))}
                {filteredOrgs.length === 0 && (
                  <div className="text-center text-sm text-muted-foreground py-6">
                    {isES ? "Sin resultados" : "No results"}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users tab */}
        <TabsContent value="users" className="mt-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchUsers} onChange={(e) => setSearchUsers(e.target.value)}
                   placeholder={isES ? "Buscar usuario por email/nombre…" : "Search user by email/name…"}
                   className="pl-9" data-testid="search-users" />
          </div>
          <Card>
            <CardContent className="pt-4">
              <div className="divide-y divide-border">
                {filteredUsers.map((u) => (
                  <div key={u.user_id}
                       className="py-3 flex items-center gap-3 flex-wrap"
                       data-testid={`user-row-${u.user_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        {u.email}
                        {u.is_super_admin && (
                          <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/30">
                            <Crown className="w-3 h-3 mr-1" /> super-admin
                          </Badge>
                        )}
                        {u.disabled && <Badge variant="destructive" className="text-[10px]">DISABLED</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {u.name && <>{u.name} · </>}
                        org: {u.organization_id || "—"} · role: {u.role || "—"}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => toggleUserDisabled(u)}
                            data-testid={`toggle-user-${u.user_id}`}
                            disabled={u.user_id === user.user_id}>
                      {u.disabled ? (isES ? "Habilitar" : "Enable") : (isES ? "Deshabilitar" : "Disable")}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setResetPwdTarget(u)}
                            data-testid={`reset-pwd-${u.user_id}`}>
                      <KeyRound className="w-3 h-3 mr-1 text-primary" />
                      {isES ? "Reset contraseña" : "Reset pwd"}
                    </Button>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <div className="text-center text-sm text-muted-foreground py-6">
                    {isES ? "Sin resultados" : "No results"}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Super-Admins tab */}
        <TabsContent value="admins" className="mt-4 space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {isES ? "Otorgar super-admin" : "Grant super-admin"}
              </CardTitle>
              <CardDescription>
                {isES
                  ? "El usuario debe estar registrado en la plataforma."
                  : "The user must be registered on the platform."}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2">
              <Input value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)}
                     placeholder="email@example.com" data-testid="grant-email-input" />
              <Button onClick={grantAdmin} disabled={!grantEmail} data-testid="grant-btn">
                <ShieldCheck className="w-4 h-4 mr-1" />
                {isES ? "Otorgar" : "Grant"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {isES ? "Super-admins activos" : "Active super-admins"} ({admins.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-border">
                {admins.map((a) => (
                  <div key={a.user_id} className="py-3 flex items-center gap-3"
                       data-testid={`admin-row-${a.user_id}`}>
                    <Crown className="w-4 h-4 text-amber-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{a.email}</div>
                      <div className="text-xs text-muted-foreground">
                        {a.name || "—"} · org: {a.organization_id || "—"}
                      </div>
                    </div>
                    {a.user_id !== user.user_id && (
                      <Button size="sm" variant="ghost" onClick={() => revokeAdmin(a.email)}
                              data-testid={`revoke-${a.user_id}`}>
                        <X className="w-3 h-3 mr-1 text-destructive" />
                        {isES ? "Revocar" : "Revoke"}
                      </Button>
                    )}
                    {a.user_id === user.user_id && (
                      <Badge variant="outline" className="text-[10px]">
                        {isES ? "Tú" : "You"}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System tab */}
        <TabsContent value="system" className="mt-4 space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{isES ? "Banner global" : "Global banner"}</CardTitle>
              <CardDescription>
                {isES
                  ? "Mensaje visible a todos los clientes (mantenimiento, anuncios, promociones)."
                  : "Message visible to all customers (maintenance, announcements, promotions)."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Switch checked={banner.enabled}
                        onCheckedChange={(v) => setBanner({ ...banner, enabled: v })}
                        data-testid="banner-enabled" />
                <span className="text-sm">
                  {banner.enabled ? (isES ? "Activado" : "Enabled") : (isES ? "Desactivado" : "Disabled")}
                </span>
              </div>
              <div>
                <Label className="text-xs">{isES ? "Severidad" : "Severity"}</Label>
                <Select value={banner.severity} onValueChange={(v) => setBanner({ ...banner, severity: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{isES ? "Mensaje" : "Message"}</Label>
                <Textarea value={banner.message || ""} rows={2}
                          onChange={(e) => setBanner({ ...banner, message: e.target.value })}
                          data-testid="banner-message" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">{isES ? "Texto link" : "Link text"}</Label>
                  <Input value={banner.link_text || ""}
                         onChange={(e) => setBanner({ ...banner, link_text: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">{isES ? "URL link" : "Link URL"}</Label>
                  <Input value={banner.link_url || ""}
                         onChange={(e) => setBanner({ ...banner, link_url: e.target.value })} />
                </div>
              </div>
              <Button onClick={saveBanner} data-testid="save-banner">
                <Save className="w-4 h-4 mr-1" /> {isES ? "Guardar" : "Save"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit log tab — widget con últimas 10 entradas + link a página completa */}
        <TabsContent value="audit" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="w-4 h-4" />
                  {isES ? "Actividad reciente" : "Recent activity"}
                </CardTitle>
                <Link to="/platform-admin/audit-log">
                  <Button variant="outline" size="sm" data-testid="open-audit-log">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    {isES ? "Abrir página completa" : "Open full page"}
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <ScrollArea className="h-[50vh]">
                <div className="divide-y divide-border">
                  {auditLog.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-6">
                      {isES ? "Sin actividad" : "No activity yet"}
                    </div>
                  )}
                  {auditLog.slice(0, 10).map((row, i) => (
                    <div key={i} className="py-2 flex items-start gap-3 text-xs">
                      <Badge variant="outline" className="shrink-0">{row.action}</Badge>
                      <div className="flex-1 min-w-0">
                        <div>{row.actor_email}</div>
                        <div className="text-muted-foreground">
                          {row.target && Object.entries(row.target).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(" · ")}
                        </div>
                      </div>
                      <div className="text-muted-foreground shrink-0">
                        {new Date(row.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit / new tier dialog */}
      <Dialog open={!!editingTier} onOpenChange={(o) => !o && setEditingTier(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tier-dialog">
          {editingTier && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {editingTier.new
                    ? (isES ? "Nuevo plan" : "New plan")
                    : (isES ? "Editar plan" : "Edit plan") + ` · ${editingTier.name}`}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">tier_id</Label>
                    <Input value={editingTier.tier_id}
                           disabled={!editingTier.new}
                           onChange={(e) => setEditingTier({ ...editingTier, tier_id: e.target.value })}
                           data-testid="tier-id-input" />
                  </div>
                  <div>
                    <Label className="text-xs">{isES ? "Nombre" : "Name"}</Label>
                    <Input value={editingTier.name}
                           onChange={(e) => setEditingTier({ ...editingTier, name: e.target.value })}
                           data-testid="tier-name-input" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">{isES ? "Precio base mensual" : "Base price/month"}</Label>
                    <Input type="number" min="0" step="1"
                           value={editingTier.price_monthly}
                           onChange={(e) => setEditingTier({ ...editingTier, price_monthly: parseFloat(e.target.value) || 0 })}
                           data-testid="tier-price-input" />
                  </div>
                  <div>
                    <Label className="text-xs">{isES ? "Color" : "Color"}</Label>
                    <Input type="color" value={editingTier.color || "#3B82F6"}
                           onChange={(e) => setEditingTier({ ...editingTier, color: e.target.value })} />
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={!!editingTier.popular}
                              onCheckedChange={(v) => setEditingTier({ ...editingTier, popular: v })} />
                      <span className="text-xs">Popular</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={!!editingTier.active}
                              onCheckedChange={(v) => setEditingTier({ ...editingTier, active: v })} />
                      <span className="text-xs">{isES ? "Activo" : "Active"}</span>
                    </div>
                  </div>
                </div>

                {/* Framework + user limits and extra pricing */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">{isES ? "Normas máximas (incluidas)" : "Frameworks max (included)"}</Label>
                    <Input type="number" min="0" step="1"
                           value={editingTier.frameworks_max ?? editingTier.included_standards ?? 0}
                           onChange={(e) => {
                             const v = parseInt(e.target.value, 10) || 0;
                             setEditingTier({ ...editingTier, frameworks_max: v, included_standards: v });
                           }}
                           data-testid="tier-frameworks-max-input" />
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {isES ? "El cliente elige cuáles activar." : "Org picks which to activate."}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">{isES ? "$/norma extra/mes" : "$/extra framework/mo"}</Label>
                    <Input type="number" min="0" step="1"
                           value={editingTier.price_per_extra_framework ?? editingTier.price_per_standard_monthly ?? 0}
                           onChange={(e) => {
                             const v = parseFloat(e.target.value) || 0;
                             setEditingTier({ ...editingTier, price_per_extra_framework: v, price_per_standard_monthly: v });
                           }}
                           data-testid="tier-extra-framework-input" />
                  </div>
                  <div>
                    <Label className="text-xs">{isES ? "Usuarios incluidos" : "Included users"}</Label>
                    <Input type="number" min="0" step="1"
                           value={editingTier.included_users ?? 0}
                           onChange={(e) => setEditingTier({ ...editingTier, included_users: parseInt(e.target.value, 10) || 0 })}
                           data-testid="tier-included-users-input" />
                  </div>
                  <div>
                    <Label className="text-xs">{isES ? "$/usuario extra" : "$/extra user"}</Label>
                    <Input type="number" min="0" step="1"
                           value={editingTier.price_per_user_monthly ?? 0}
                           onChange={(e) => setEditingTier({ ...editingTier, price_per_user_monthly: parseFloat(e.target.value) || 0 })}
                           data-testid="tier-ppu-input" />
                  </div>
                </div>

                {/* Sprint 4F: trial */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">{isES ? "Dias de prueba" : "Trial days"}</Label>
                    <Input type="number" min="0" step="1"
                           value={editingTier.trial_days ?? 0}
                           onChange={(e) => setEditingTier({ ...editingTier, trial_days: parseInt(e.target.value, 10) || 0 })}
                           data-testid="tier-trial-days-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{isES ? "Descripción" : "Description"}</Label>
                  <Textarea rows={2} value={editingTier.description || ""}
                            onChange={(e) => setEditingTier({ ...editingTier, description: e.target.value })} />
                </div>

                {/* Limits */}
                <div>
                  <Label className="text-xs font-semibold">{isES ? "Límites" : "Limits"}</Label>
                  <p className="text-[10px] text-muted-foreground mb-1">
                    {isES ? "Usa -1 para ilimitado." : "Use -1 for unlimited."}
                  </p>
                  <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto">
                    {knownFlags.limits.map((k) => (
                      <div key={k} className="flex items-center gap-2">
                        <Label className="text-xs flex-1 truncate">{k}</Label>
                        <Input type="number" className="w-24 h-8"
                               value={editingTier.limits?.[k] ?? 0}
                               onChange={(e) => setEditingTier({
                                 ...editingTier,
                                 limits: { ...editingTier.limits, [k]: parseInt(e.target.value, 10) || 0 },
                               })} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div>
                  <Label className="text-xs font-semibold">{isES ? "Funcionalidades" : "Features"}</Label>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 max-h-60 overflow-y-auto">
                    {knownFlags.features.map((k) => (
                      <div key={k} className="flex items-center gap-2 py-1">
                        <Switch checked={!!editingTier.features?.[k]}
                                onCheckedChange={(v) => setEditingTier({
                                  ...editingTier,
                                  features: { ...editingTier.features, [k]: v },
                                })}
                                data-testid={`feat-${k}`} />
                        <span className="text-xs truncate flex-1">{k}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingTier(null)}>
                  {isES ? "Cancelar" : "Cancel"}
                </Button>
                <Button onClick={saveTier} data-testid="save-tier">
                  <Save className="w-4 h-4 mr-1" />
                  {isES ? "Guardar" : "Save"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* S14a — Create Organization Dialog */}
      <Dialog open={createOrgOpen} onOpenChange={(o) => !o && setCreateOrgOpen(false)}>
        <DialogContent className="max-w-md" data-testid="create-org-dialog">
          <DialogHeader>
            <DialogTitle>{isES ? "Nuevo tenant" : "New tenant"}</DialogTitle>
            <DialogDescription>
              {isES
                ? "El owner recibirá un enlace por email para definir su contraseña."
                : "The owner will receive an email link to set their password."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateOrg} className="space-y-3">
            <div>
              <Label className="text-xs">{isES ? "Nombre de la organización" : "Organization name"}</Label>
              <Input
                value={createOrgForm.name}
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, name: e.target.value })}
                required
                data-testid="create-org-name"
              />
            </div>
            <div>
              <Label className="text-xs">{isES ? "Email del owner" : "Owner email"}</Label>
              <Input
                type="email"
                value={createOrgForm.owner_email}
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, owner_email: e.target.value })}
                required
                data-testid="create-org-email"
              />
            </div>
            <div>
              <Label className="text-xs">{isES ? "Nombre del owner (opcional)" : "Owner name (optional)"}</Label>
              <Input
                value={createOrgForm.owner_name}
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, owner_name: e.target.value })}
                data-testid="create-org-owner-name"
              />
            </div>
            <div>
              <Label className="text-xs">{isES ? "Plan (opcional)" : "Tier (optional)"}</Label>
              <Select value={createOrgForm.tier_id} onValueChange={(v) => setCreateOrgForm({ ...createOrgForm, tier_id: v })}>
                <SelectTrigger data-testid="create-org-tier">
                  <SelectValue placeholder={isES ? "Sin plan asignado" : "No tier assigned"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{isES ? "Sin plan" : "No tier"}</SelectItem>
                  {tiers.map((t) => (
                    <SelectItem key={t.tier_id} value={t.tier_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setCreateOrgOpen(false)}>
                {isES ? "Cancelar" : "Cancel"}
              </Button>
              <Button type="submit" disabled={createOrgLoading} data-testid="create-org-submit">
                {createOrgLoading ? (isES ? "Creando..." : "Creating...") : (isES ? "Crear" : "Create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* S14a — Delete Organization (ConfirmDestructiveDialog) */}
      <ConfirmDestructiveDialog
        open={!!deleteOrgTarget}
        onClose={() => setDeleteOrgTarget(null)}
        title={isES ? "Eliminar organización (soft delete)" : "Delete organization (soft delete)"}
        description={
          <div className="space-y-2">
            <p>{isES
              ? `Esta acción es reversible hasta 30 días. Los datos se conservan pero el acceso queda bloqueado inmediatamente.`
              : `This action is reversible for 30 days. Data is retained but access is blocked immediately.`}
            </p>
            {deleteOrgTarget && (
              <p className="text-xs text-muted-foreground">
                {isES ? "Organización:" : "Organization:"} <strong>{deleteOrgTarget.name}</strong>
              </p>
            )}
          </div>
        }
        expectedConfirmText={deleteOrgTarget?.name || ""}
        confirmLabel={isES ? "Eliminar" : "Delete"}
        onConfirm={handleDeleteOrg}
        loading={deleteOrgLoading}
        danger={true}
      />

      {/* S14a — Restore Organization Dialog */}
      <Dialog open={!!restoreOrgTarget} onOpenChange={(o) => !o && setRestoreOrgTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="restore-org-dialog">
          <DialogHeader>
            <DialogTitle>{isES ? "Restaurar organización" : "Restore organization"}</DialogTitle>
            <DialogDescription>
              {isES
                ? `¿Restaurar "${restoreOrgTarget?.name}" y reactivar el acceso de sus usuarios?`
                : `Restore "${restoreOrgTarget?.name}" and reactivate access for its users?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRestoreOrgTarget(null)}>
              {isES ? "Cancelar" : "Cancel"}
            </Button>
            <Button onClick={handleRestoreOrg} disabled={restoreOrgLoading} data-testid="restore-org-confirm">
              {restoreOrgLoading ? (isES ? "Restaurando..." : "Restoring...") : (isES ? "Restaurar" : "Restore")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* S14a — Admin Reset Password Dialog */}
      <Dialog open={!!resetPwdTarget} onOpenChange={(o) => !o && setResetPwdTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="reset-pwd-dialog">
          <DialogHeader>
            <DialogTitle>{isES ? "Resetear contraseña" : "Reset password"}</DialogTitle>
            <DialogDescription>
              {isES
                ? `Se enviará un enlace de restablecimiento a ${resetPwdTarget?.email}. El administrador no verá la nueva contraseña.`
                : `A reset link will be sent to ${resetPwdTarget?.email}. The admin will not see the new password.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResetPwdTarget(null)}>
              {isES ? "Cancelar" : "Cancel"}
            </Button>
            <Button onClick={handleAdminResetPassword} disabled={resetPwdLoading} data-testid="reset-pwd-confirm">
              {resetPwdLoading ? (isES ? "Enviando..." : "Sending...") : (isES ? "Enviar enlace" : "Send link")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P2 — Tier override form (reemplaza 3 window.prompt secuenciales, §2c) */}
      <Dialog open={!!overrideTarget} onOpenChange={(o) => !o && setOverrideTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="override-tier-dialog">
          <DialogHeader>
            <DialogTitle>{t("padmin.override_title")}</DialogTitle>
            {overrideTarget && (
              <DialogDescription>{overrideTarget.name}</DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">{t("padmin.override_tier_label")}</Label>
              <Select
                value={overrideForm.tier}
                onValueChange={(v) => setOverrideForm({ ...overrideForm, tier: v })}
              >
                <SelectTrigger data-testid="override-tier-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tiers.map((ti) => (
                    <SelectItem key={ti.tier_id} value={ti.tier_id}>{ti.tier_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("padmin.override_price_label")}</Label>
              <Input
                type="number"
                value={overrideForm.priceOverride}
                onChange={(e) => setOverrideForm({ ...overrideForm, priceOverride: e.target.value })}
                data-testid="override-price-input"
              />
            </div>
            <div>
              <Label className="text-xs">{t("padmin.override_note_label")}</Label>
              <Input
                value={overrideForm.note}
                onChange={(e) => setOverrideForm({ ...overrideForm, note: e.target.value })}
                data-testid="override-note-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOverrideTarget(null)}>
              {isES ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={submitOverrideTier}
              disabled={overrideLoading || !overrideForm.tier}
              data-testid="override-tier-submit"
            >
              {overrideLoading ? (isES ? "Guardando..." : "Saving...") : t("padmin.override_save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =====================================================================
// Integrations tab: AI provider + system SMTP (super-admin only)
// =====================================================================

const AI_MODELS = {
  anthropic: [
    { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (recommended)" },
    { id: "claude-opus-4-7", label: "Claude Opus 4.7" },
    { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
  ],
  openai: [
    { id: "gpt-4o", label: "GPT-4o" },
    { id: "gpt-4o-mini", label: "GPT-4o mini (recommended)" },
    { id: "gpt-4-turbo", label: "GPT-4 Turbo" },
  ],
};

const IntegrationsTab = ({ token, isES }) => {
  const [loading, setLoading] = useState(true);
  const [savingAi, setSavingAi] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingAi, setTestingAi] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testTo, setTestTo] = useState("");

  const [ai, setAi] = useState({
    provider: "anthropic",
    api_key: "",
    api_key_set: false,
    model: "",
    max_tokens: 2048,
  });
  const [smtp, setSmtp] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    password_set: false,
    from_email: "",
    from_name: "ComplianceOS by MSSPRD",
    use_tls: true,
    use_ssl: false,
  });

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/platform/integrations`, auth);
      setAi({
        provider: r.data.ai.provider || "anthropic",
        api_key: "",
        api_key_set: !!r.data.ai.api_key_set,
        model: r.data.ai.model || "",
        max_tokens: r.data.ai.max_tokens || 2048,
      });
      setSmtp({
        host: r.data.smtp.host || "",
        port: r.data.smtp.port || 587,
        username: r.data.smtp.username || "",
        password: "",
        password_set: !!r.data.smtp.password_set,
        from_email: r.data.smtp.from_email || "",
        from_name: r.data.smtp.from_name || "ComplianceOS by MSSPRD",
        use_tls: r.data.smtp.use_tls ?? true,
        use_ssl: r.data.smtp.use_ssl ?? false,
      });
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al cargar integraciones" : "Failed to load integrations"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveAi = async () => {
    const tokensNum = Number(ai.max_tokens) || 2048;
    // Validate range before sending to avoid a 422 that crashes via object-in-toast
    if (tokensNum < 64 || tokensNum > 200000) {
      toast.error(isES ? "Maximo de tokens debe estar entre 64 y 200 000" : "Max tokens must be between 64 and 200 000");
      return;
    }
    setSavingAi(true);
    try {
      const body = {
        provider: ai.provider,
        model: ai.model || undefined,
        max_tokens: tokensNum,
      };
      if (ai.api_key) body.api_key = ai.api_key;
      await axios.put(`${API}/platform/integrations/ai`, body, auth);
      toast.success(isES ? "Configuracion de IA guardada" : "AI configuration saved");
      await fetchData();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al guardar" : "Save failed"));
    } finally { setSavingAi(false); }
  };

  const testAi = async () => {
    setTestingAi(true);
    try {
      const body = { provider: ai.provider, model: ai.model || undefined };
      if (ai.api_key) body.api_key = ai.api_key;
      const r = await axios.post(`${API}/platform/integrations/ai/test`, body, auth);
      if (r.data.ok) toast.success(`OK - ${r.data.sample || "AI provider reachable"}`);
      else toast.error(`Failed: ${r.data.error || "unknown"}`);
    } catch (e) {
      toast.error(extractError(e, "Test failed"));
    } finally { setTestingAi(false); }
  };

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      const body = { ...smtp };
      if (!body.password) delete body.password;
      delete body.password_set;
      await axios.put(`${API}/platform/integrations/smtp`, body, auth);
      toast.success(isES ? "SMTP guardado" : "SMTP saved");
      await fetchData();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al guardar" : "Save failed"));
    } finally { setSavingSmtp(false); }
  };

  const testSmtp = async () => {
    if (!testTo) {
      toast.error(isES ? "Indica un correo destino" : "Enter a recipient email");
      return;
    }
    setTestingSmtp(true);
    try {
      const body = { ...smtp, to_email: testTo };
      if (!body.password) delete body.password;
      delete body.password_set;
      const r = await axios.post(`${API}/platform/integrations/smtp/test`, body, auth);
      if (r.data.ok) toast.success(isES ? "Correo enviado" : "Email sent");
      else toast.error(isES ? "Envio fallo" : "Send failed");
    } catch (e) {
      toast.error(extractError(e, isES ? "Prueba fallida" : "Test failed"));
    } finally { setTestingSmtp(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>;
  }

  const models = AI_MODELS[ai.provider] || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* AI provider card */}
      <Card data-testid="ai-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Brain className="w-5 h-5 text-primary" />
            {isES ? "Proveedor de IA" : "AI Provider"}
          </CardTitle>
          <CardDescription>
            {isES
              ? "Selecciona el proveedor de IA para el asistente, generador de politicas, predictor de riesgo y wizard."
              : "Select the AI provider that powers the chat assistant, policy generator, risk predictor and wizard."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>{isES ? "Proveedor" : "Provider"}</Label>
            <Select value={ai.provider} onValueChange={(v) => setAi({ ...ai, provider: v, model: "" })}>
              <SelectTrigger data-testid="ai-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                <SelectItem value="openai">OpenAI (ChatGPT)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>{isES ? "Modelo" : "Model"}</Label>
            <Select value={ai.model || ""} onValueChange={(v) => setAi({ ...ai, model: v })}>
              <SelectTrigger data-testid="ai-model-select">
                <SelectValue placeholder={isES ? "Selecciona modelo" : "Pick a model"} />
              </SelectTrigger>
              <SelectContent>
                {models.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>{isES ? "Clave de API" : "API key"}</Label>
            <Input
              type="password" autoComplete="off"
              placeholder={ai.api_key_set ? (isES ? "(guardada - deja vacio para conservar)" : "(saved - leave empty to keep)") : "sk-..."}
              value={ai.api_key}
              onChange={(e) => setAi({ ...ai, api_key: e.target.value })}
              data-testid="ai-api-key-input"
            />
            {ai.api_key_set && (
              <p className="text-xs text-primary flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> {isES ? "Una clave esta guardada" : "A key is currently saved"}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label>{isES ? "Maximo de tokens" : "Max tokens"}</Label>
            <Input
              type="number" min={64} max={200000}
              value={ai.max_tokens}
              onChange={(e) => setAi({ ...ai, max_tokens: e.target.value })}
              data-testid="ai-max-tokens"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={saveAi} disabled={savingAi} data-testid="ai-save-btn">
              {savingAi ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              {isES ? "Guardar" : "Save"}
            </Button>
            <Button variant="outline" onClick={testAi} disabled={testingAi || (!ai.api_key && !ai.api_key_set)} data-testid="ai-test-btn">
              {testingAi ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              {isES ? "Probar" : "Test"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SMTP card */}
      <Card data-testid="smtp-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="w-5 h-5 text-primary" />
            {isES ? "SMTP del sistema" : "System SMTP"}
          </CardTitle>
          <CardDescription>
            {isES
              ? "SMTP usado para correos transaccionales (confirmacion de registro, bienvenida, restablecer clave)."
              : "SMTP used for transactional emails (signup confirmation, welcome, password reset)."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1 col-span-2">
              <Label>{isES ? "Servidor" : "Host"}</Label>
              <Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
                placeholder="smtp.gmail.com" data-testid="smtp-host" />
            </div>
            <div className="space-y-1">
              <Label>{isES ? "Puerto" : "Port"}</Label>
              <Input type="number" value={smtp.port}
                onChange={(e) => setSmtp({ ...smtp, port: Number(e.target.value) })}
                data-testid="smtp-port" />
            </div>
            <div className="space-y-1">
              <Label>TLS</Label>
              <div className="flex items-center gap-3 pt-2">
                <Switch checked={!!smtp.use_tls} onCheckedChange={(v) => setSmtp({ ...smtp, use_tls: v, use_ssl: v ? false : smtp.use_ssl })} />
                <span className="text-xs text-muted-foreground">SSL</span>
                <Switch checked={!!smtp.use_ssl} onCheckedChange={(v) => setSmtp({ ...smtp, use_ssl: v, use_tls: v ? false : smtp.use_tls })} />
              </div>
            </div>
            <div className="space-y-1 col-span-2">
              <Label>{isES ? "Usuario" : "Username"}</Label>
              <Input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })}
                autoComplete="off" data-testid="smtp-username" />
            </div>
            <div className="space-y-1 col-span-2">
              <Label>{isES ? "Contrasena" : "Password"}</Label>
              <Input type="password" autoComplete="off"
                placeholder={smtp.password_set ? (isES ? "(guardada - deja vacio para conservar)" : "(saved - leave empty to keep)") : ""}
                value={smtp.password}
                onChange={(e) => setSmtp({ ...smtp, password: e.target.value })}
                data-testid="smtp-password" />
            </div>
            <div className="space-y-1">
              <Label>{isES ? "Remitente" : "From email"}</Label>
              <Input value={smtp.from_email}
                onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })}
                placeholder="no-reply@complianceos.mssprd.app" data-testid="smtp-from-email" />
            </div>
            <div className="space-y-1">
              <Label>{isES ? "Nombre" : "From name"}</Label>
              <Input value={smtp.from_name}
                onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })}
                data-testid="smtp-from-name" />
            </div>
          </div>

          <div className="pt-2 border-t border-border space-y-2">
            <Label className="text-xs text-muted-foreground">
              {isES ? "Enviar correo de prueba" : "Send a test email"}
            </Label>
            <div className="flex gap-2">
              <Input type="email" placeholder="you@company.com"
                value={testTo} onChange={(e) => setTestTo(e.target.value)}
                data-testid="smtp-test-to" />
              <Button variant="outline" onClick={testSmtp}
                disabled={testingSmtp} data-testid="smtp-test-btn">
                {testingSmtp ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                {isES ? "Probar" : "Test"}
              </Button>
            </div>
          </div>

          <div className="pt-2">
            <Button onClick={saveSmtp} disabled={savingSmtp} data-testid="smtp-save-btn">
              {savingSmtp ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              {isES ? "Guardar SMTP" : "Save SMTP"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------- PayPal Billing tab (Sprint 4E) ----------
const PayPalBillingTab = ({ token, isES }) => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    client_id: "", client_secret: "", webhook_id: "", environment: "sandbox",
  });
  const [saving, setSaving] = useState(false);
  const authH = { headers: { Authorization: `Bearer ${token}` } };

  const fetchCfg = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/billing/paypal/admin/config`, authH);
      setCfg(res.data);
      setForm((f) => ({
        ...f,
        client_id: res.data.client_id || "",
        webhook_id: res.data.webhook_id || "",
        environment: res.data.environment || "sandbox",
      }));
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al cargar" : "Load failed"));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id || undefined,
        client_secret: form.client_secret || undefined,
        webhook_id: form.webhook_id || undefined,
        environment: form.environment || undefined,
      };
      await axios.put(`${API}/billing/paypal/admin/config`, payload, authH);
      toast.success(isES ? "Configuracion guardada" : "Configuration saved");
      setForm((f) => ({ ...f, client_secret: "" }));
      fetchCfg();
    } catch (e) {
      toast.error(extractError(e, isES ? "Error al guardar" : "Failed"));
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-primary" />
          {isES ? "Integracion PayPal" : "PayPal Integration"}
        </CardTitle>
        <CardDescription>
          {isES
            ? "Configura las credenciales del API para procesar pagos. El secreto nunca se devuelve al navegador."
            : "Configure API credentials to process payments. The secret is never returned to the browser."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Client ID</Label>
            <Input value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              placeholder="AY1...sandboxClientId" data-testid="paypal-client-id" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              Client Secret {cfg?.client_secret_masked && (
                <span className="ml-2 text-muted-foreground">
                  ({isES ? "actual" : "current"}: {cfg.client_secret_masked})
                </span>
              )}
            </Label>
            <Input type="password" value={form.client_secret}
              onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
              placeholder={isES ? "Dejar vacio para conservar" : "Leave blank to keep current"}
              data-testid="paypal-client-secret" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Webhook ID</Label>
            <Input value={form.webhook_id}
              onChange={(e) => setForm({ ...form, webhook_id: e.target.value })}
              placeholder="WH-..." data-testid="paypal-webhook-id" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{isES ? "Entorno" : "Environment"}</Label>
            <select value={form.environment}
              onChange={(e) => setForm({ ...form, environment: e.target.value })}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              data-testid="paypal-env">
              <option value="sandbox">Sandbox</option>
              <option value="live">Live (production)</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving} data-testid="save-paypal-cfg">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {isES ? "Guardar" : "Save"}
          </Button>
          <Badge variant={cfg?.configured ? "default" : "secondary"}>
            {cfg?.configured
              ? (isES ? "Listo" : "Configured")
              : (isES ? "No configurado" : "Not configured")}
          </Badge>
          {cfg?.environment && (
            <Badge variant="outline" className="capitalize">{cfg.environment}</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {isES
            ? "Webhook URL: POST /api/billing/paypal/webhook  ·  Eventos: CHECKOUT.ORDER.APPROVED, PAYMENT.CAPTURE.COMPLETED"
            : "Webhook URL: POST /api/billing/paypal/webhook  ·  Events: CHECKOUT.ORDER.APPROVED, PAYMENT.CAPTURE.COMPLETED"}
        </p>
      </CardContent>
    </Card>
  );
};

// ============================================================
// Demo Requests tab — sales pipeline view of /api/contact/demo-request
// ============================================================
const DEMO_STATUS_META = {
  new:        { es: "Nueva",       en: "New",        color: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  contacted:  { es: "Contactado",  en: "Contacted",  color: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  qualified:  { es: "Calificado",  en: "Qualified",  color: "bg-violet-500/15 text-violet-400 border-violet-500/30" },
  won:        { es: "Ganado",      en: "Won",        color: "bg-green-500/15 text-green-400 border-green-500/30" },
  lost:       { es: "Perdido",     en: "Lost",       color: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};

const DemoRequestsTab = ({ token, isES }) => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [savingId, setSavingId] = useState("");
  const [notesDraft, setNotesDraft] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const r = await axios.get(`${API}/platform/demo-requests`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      toast.error(isES ? "Error cargando solicitudes" : "Error loading requests");
    } finally { setLoading(false); }
  }, [token, statusFilter, search, isES]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateRequest = async (request_id, patch) => {
    setSavingId(request_id);
    try {
      const r = await axios.patch(`${API}/platform/demo-requests/${request_id}`,
        patch, { headers: { Authorization: `Bearer ${token}` } });
      setItems((prev) => prev.map((it) => it.request_id === request_id ? { ...it, ...r.data } : it));
      if (selected?.request_id === request_id) setSelected({ ...selected, ...r.data });
      toast.success(isES ? "Actualizado" : "Updated");
      fetchAll();
    } catch (e) {
      toast.error(isES ? "No se pudo actualizar" : "Update failed");
    } finally { setSavingId(""); }
  };

  const openDetail = (item) => {
    setSelected(item);
    setNotesDraft(item.notes || "");
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={isES ? "Buscar por empresa / email / nombre" : "Search company / email / name"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") fetchAll(); }}
            data-testid="demo-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="demo-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isES ? "Todos" : "All"} ({counts.total || 0})</SelectItem>
            {Object.keys(DEMO_STATUS_META).map((s) => (
              <SelectItem key={s} value={s}>
                {DEMO_STATUS_META[s][isES ? "es" : "en"]} ({counts[s] || 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (isES ? "Refrescar" : "Refresh")}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {items.length === 0 && !loading ? (
            <div className="text-center text-sm text-muted-foreground py-16">
              <Inbox className="w-8 h-8 mx-auto mb-3 opacity-40" />
              {isES ? "No hay solicitudes que coincidan." : "No requests match the filter."}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {items.map((it) => {
                const meta = DEMO_STATUS_META[it.status] || DEMO_STATUS_META.new;
                return (
                  <div
                    key={it.request_id}
                    className="flex items-center gap-3 p-3 hover:bg-muted/40 cursor-pointer"
                    onClick={() => openDetail(it)}
                    data-testid={`demo-row-${it.request_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{it.company || "—"}</span>
                        <Badge variant="outline" className={`text-xs ${meta.color}`}>
                          {meta[isES ? "es" : "en"]}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {it.name} · {it.email} · {it.sector || "—"} · {it.company_size || "—"}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground hidden md:block">
                      {new Date(it.created_at).toLocaleString(isES ? "es-DO" : "en-US")}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.company}
                  <Badge variant="outline" className={`text-xs ${(DEMO_STATUS_META[selected.status] || DEMO_STATUS_META.new).color}`}>
                    {(DEMO_STATUS_META[selected.status] || DEMO_STATUS_META.new)[isES ? "es" : "en"]}
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  {selected.request_id} · {new Date(selected.created_at).toLocaleString(isES ? "es-DO" : "en-US")}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div><Label className="text-xs text-muted-foreground">{isES ? "Contacto" : "Contact"}</Label>
                  <div>{selected.name}</div></div>
                <div><Label className="text-xs text-muted-foreground">Email</Label>
                  <div><a href={`mailto:${selected.email}`} className="text-primary hover:underline">{selected.email}</a></div></div>
                <div><Label className="text-xs text-muted-foreground">{isES ? "Teléfono" : "Phone"}</Label>
                  <div>{selected.phone || "—"}</div></div>
                <div><Label className="text-xs text-muted-foreground">{isES ? "Sector" : "Sector"}</Label>
                  <div>{selected.sector || "—"}</div></div>
                <div><Label className="text-xs text-muted-foreground">{isES ? "Tamaño" : "Size"}</Label>
                  <div>{selected.company_size || "—"}</div></div>
                <div><Label className="text-xs text-muted-foreground">{isES ? "Idioma" : "Locale"}</Label>
                  <div className="uppercase">{selected.locale || "es"}</div></div>
              </div>

              {selected.message && (
                <div>
                  <Label className="text-xs text-muted-foreground">{isES ? "Mensaje del prospecto" : "Prospect message"}</Label>
                  <div className="mt-1 p-3 bg-muted/40 rounded text-sm whitespace-pre-wrap">{selected.message}</div>
                </div>
              )}

              <div>
                <Label className="text-xs text-muted-foreground">{isES ? "Notas internas" : "Internal notes"}</Label>
                <Textarea
                  rows={4}
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder={isES ? "Resumen de llamadas, próximos pasos…" : "Call summaries, next steps…"}
                  data-testid="demo-notes"
                />
                <div className="flex justify-end mt-1">
                  <Button size="sm" variant="outline" disabled={savingId === selected.request_id}
                    onClick={() => updateRequest(selected.request_id, { notes: notesDraft })}>
                    {isES ? "Guardar notas" : "Save notes"}
                  </Button>
                </div>
              </div>

              <DialogFooter className="flex-wrap gap-2">
                <div className="flex items-center gap-2 mr-auto">
                  <Label className="text-xs text-muted-foreground">{isES ? "Estado:" : "Status:"}</Label>
                  <Select value={selected.status}
                          onValueChange={(value) => updateRequest(selected.request_id, { status: value })}>
                    <SelectTrigger className="w-[160px]" data-testid="demo-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(DEMO_STATUS_META).map((s) => (
                        <SelectItem key={s} value={s}>{DEMO_STATUS_META[s][isES ? "es" : "en"]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button asChild variant="outline" size="sm">
                  <a href={`mailto:${selected.email}?subject=${encodeURIComponent("ComplianceOS Demo")}`}>
                    <Mail className="w-3.5 h-3.5 mr-1" />{isES ? "Responder por correo" : "Reply by email"}
                  </a>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                  {isES ? "Cerrar" : "Close"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PlatformAdmin;
